import { FC, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, IconButton, InputAdornment, styled } from '@mui/material';
import {
  DateTimePicker as MuiDateTimePicker,
  DateTimePickerProps as MuiDateTimePickerProps,
  DateTimeValidationError,
  pickersSectionListClasses,
} from '@mui/x-date-pickers';
import dayjs from 'dayjs';

import { Icon } from '@components/base';

export type DateTimePickerProps = Pick<
  MuiDateTimePickerProps,
  | 'autoFocus'
  | 'className'
  | 'disabled'
  | 'disableFuture'
  | 'disablePast'
  | 'label'
  | 'loading'
  | 'maxDate'
  | 'maxDateTime'
  | 'maxTime'
  | 'minDate'
  | 'minDateTime'
  | 'minTime'
  | 'onAccept'
  | 'onClose'
  | 'onError'
  | 'onOpen'
  | 'open'
  | 'orientation'
  | 'readOnly'
  | 'shouldDisableDate'
  | 'shouldDisableMonth'
  | 'shouldDisableTime'
  | 'shouldDisableYear'
  | 'slotProps'
  | 'slots'
  | 'sx'
> & {
  disableClearable?: boolean;
  fullWidth?: boolean;
  hideErrorMessage?: boolean;
  onChange: (newValue: null | string) => void;
  value: null | string;
};

const StyledDateTimePicker = styled(MuiDateTimePicker)`
  .${pickersSectionListClasses.root} {
    padding: 8.5px 0;
  }

  .clear-btn {
    opacity: 0;
  }

  &:hover,
  &:focus,
  &:focus-within,
  &:active {
    .clear-btn {
      opacity: 1;
    }
  }
` as typeof MuiDateTimePicker;

export const DateTimePicker: FC<DateTimePickerProps> = ({
  disableClearable,
  fullWidth,
  hideErrorMessage,
  onChange,
  onError,
  slotProps,
  slots,
  value,
  ...rest
}) => {
  const { t } = useTranslation();

  const [key, setKey] = useState(() => Date.now());
  const [error, setError] = useState<DateTimeValidationError | undefined>(undefined);

  const btnRef = useRef<HTMLButtonElement>(null);

  const timerRef = useRef<null | ReturnType<typeof setTimeout>>(null);

  useEffect(() => {
    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
        timerRef.current = null;
      }
    };
  }, []);

  const clear = () => {
    onChange(null);
    setKey(Date.now());
    setError(undefined);
    timerRef.current = setTimeout(() => btnRef.current?.focus(), 1);
  };

  const { maxDate, maxDateTime, minDate, minDateTime } = rest;

  const getErrorContext = () => {
    switch (error) {
      case 'maxDate':
      case 'maxTime':
        return maxDate || maxDateTime ? 'endTimeBeforeStartTime' : error;
      case 'minDate':
      case 'minTime':
        return minDate || minDateTime ? 'startTimeAfterEndTime' : error;
      default:
        return error;
    }
  };

  return (
    <StyledDateTimePicker
      ampm={false}
      format={t('dateTimeFormat')}
      key={key}
      onChange={newValue =>
        onChange && onChange(newValue && newValue.isValid() ? newValue.toISOString() : null)
      }
      onError={(err, val) => {
        onError?.(err, val);
        setError(err);
      }}
      slotProps={
        hideErrorMessage
          ? undefined
          : {
              ...slotProps,
              textField: {
                ...slotProps?.textField,
                helperText: error ? t('dateError', { context: getErrorContext() }) : undefined,
                fullWidth,
              },
              openPickerButton: { size: 'small' },
              openPickerIcon: { fontSize: 'small' },
            }
      }
      slots={{
        ...slots,
        inputAdornment: disableClearable
          ? undefined
          : ({ position, ...props }) => (
              <InputAdornment position={position} sx={{ mr: -1 }}>
                <IconButton
                  className='clear-btn'
                  onClick={clear}
                  ref={btnRef}
                  size='small'
                  title={t('clear')}
                >
                  <Icon fontSize='inherit' name='close' />
                </IconButton>

                <Box {...props} />
              </InputAdornment>
            ),
      }}
      value={value ? dayjs(value) : null}
      {...rest}
    />
  );
};
